import { cx, Panel } from "@amber/ui";
import type { AllowanceRow, BudgetsResponse } from "../../contracts/index";
import { amount, num, usd } from "./money";
import { Name, TONE_TEXT, Table, Td, Th, ToneIcon } from "./parts";

function pctOf(value: number, of: number): number {
  if (of > 0) return Math.min(100, (value / of) * 100);
  return value > 0 ? 100 : 0;
}

function Meter({ row, alert, suspend }: { row: AllowanceRow; alert: number; suspend: number }) {
  const allowance = num(row.allowance);
  const incurred = pctOf(num(row.incurred), allowance);
  const committed = Math.min(100 - incurred, pctOf(num(row.committed), allowance));
  const forecast = pctOf(num(row.forecast), allowance);
  return (
    <div
      role="img"
      aria-label={`${row.name}: ${usd(row.incurred)} spent and ${usd(row.committed)} committed of ${usd(row.allowance)}; forecast ${usd(row.forecast)}`}
      className="relative h-2 min-w-[110px] rounded-full bg-surface-3"
    >
      <span className="absolute inset-y-0 left-0 rounded-l-full bg-link" style={{ width: `${String(incurred)}%` }} />
      <span
        className="absolute inset-y-0 bg-link/40"
        style={{ left: `${String(incurred)}%`, width: `${String(committed)}%` }}
      />
      <span className="absolute -inset-y-0.5 w-px bg-muted" style={{ left: `${String(alert)}%` }} />
      <span className="absolute -inset-y-0.5 w-px bg-muted" style={{ left: `${String(suspend)}%` }} />
      <span
        className={cx("absolute -inset-y-1 w-0.5 rounded-[1px]", row.over ? "bg-waiting" : "bg-fg")}
        style={{ left: `calc(${String(forecast)}% - 1px)` }}
      />
    </div>
  );
}

export function AllowancesPanel({ data }: { data: BudgetsResponse }) {
  const { alert_percent: alert, suspend_percent: suspend } = data.thresholds;
  const ordered = [
    ...data.allowances.filter((a) => a.key !== "total"),
    ...data.allowances.filter((a) => a.key === "total"),
  ];
  return (
    <Panel
      title="Monthly allowances"
      note={`${data.period.month}, day ${String(data.period.day)} of ${String(data.period.days)} · spent and committed against each allowance`}
    >
      <Table
        label="Monthly allowances"
        minWidth={760}
        head={
          <>
            <Th className="w-[27%]">Category</Th>
            <Th right>Allowance</Th>
            <Th className="min-w-[200px]">Spent and committed (USD)</Th>
            <Th right>Forecast at month end</Th>
            <Th className="min-w-[160px]">Now</Th>
          </>
        }
      >
        {ordered.map((row) => {
          const total = row.key === "total";
          return (
            <tr key={row.key} className={total ? "[&>td]:bg-surface-2" : undefined}>
              <Td>
                <Name detail={row.covers}>{row.name}</Name>
              </Td>
              <Td right className={cx("tabular-nums", total ? "font-semibold text-fg" : "text-fg")}>
                {usd(row.allowance)}
              </Td>
              <Td>
                <div className="grid grid-cols-[minmax(110px,1fr)_auto] items-center gap-2.5">
                  <Meter row={row} alert={alert} suspend={suspend} />
                  <span className="font-mono text-md whitespace-nowrap">
                    <b className="font-semibold text-fg">{amount(num(row.incurred) + num(row.committed))}</b> ·{" "}
                    {row.percent}%
                  </span>
                </div>
              </Td>
              <Td right className={cx("tabular-nums", row.over ? "text-waiting" : "text-fg")}>
                {usd(row.forecast)}
              </Td>
              <Td>
                <span className={cx("inline-flex items-start gap-1.5", TONE_TEXT[row.state.tone])}>
                  {row.state.tone === "ok" ? null : <ToneIcon tone={row.state.tone} />}
                  {row.state.label}
                </span>
              </Td>
            </tr>
          );
        })}
      </Table>
      <div aria-hidden="true" className="mt-2 flex flex-wrap gap-3 text-xs text-muted">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-[7px] w-3 rounded-[2px] bg-link" />
          Spent
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-[7px] w-3 rounded-[2px] bg-link/40" />
          Committed
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-[11px] w-0.5 bg-fg" />
          Forecast
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-px bg-muted" />
          Alert {alert}% · suspend {suspend}%
        </span>
      </div>
      <p className="m-0 mt-2.5 max-w-[780px] text-ui text-muted">
        A category over its allowance stays visible even when the total is within budget. Budget controls defer new
        optional work; they never expire or delete evidence already collected.
      </p>
    </Panel>
  );
}
